import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './index.scss';
import iconDonation from '../../../assets/icon-donation-grey.png';
import iconDove from '../../../assets/icon-dove-grey.png';
import iconPraying from '../../../assets/icon-praying-grey.png';
import Payment, { DonationFrequency, DonationSource } from '../../../models/Payment';
import PaymentCheckout from '../../../models/PaymentCheckout';
import PaymentResponse from '../../../models/PaymentResponse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleCheck,
    faCircleExclamation,
    faCircleInfo,
    faHeart,
    faLock,
    faSpinner,
} from '@fortawesome/pro-solid-svg-icons';

const MIN_AMOUNT = 1;
const MAX_AMOUNT = 10000;

const PRESETS: Record<DonationFrequency, number[]> = {
    once: [15, 30, 50, 100],
    month: [5, 10, 25, 50],
};

// Recurring gifts are worth more to the foundation, so monthly is preselected and one-time is an opt-out.
const DEFAULT_FREQUENCY: DonationFrequency = 'month';

const DEFAULT_PRESET: Record<DonationFrequency, number> = {
    once: 30,
    month: 10,
};

// Bancontact and EPS can't carry a recurring mandate, so Stripe drops them from subscriptions.
const TRUST_METHODS: Record<DonationFrequency, string> = {
    once: 'iDEAL | Wero \u00B7 Creditcard \u00B7 Apple Pay \u00B7 Google Pay \u00B7 Bancontact',
    month: 'iDEAL | Wero \u00B7 Creditcard \u00B7 Apple Pay \u00B7 Google Pay',
};

// Matches iDEAL, the cheapest method and the one most donors pick. Card gifts cost us more,
// but quoting the low end means nobody is ever asked for more than their gift actually costs.
const COVER_FEE = 0.29;

const wholeCurrency = new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

const centsCurrency = new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

// Whole euros stay clean ("€ 30"); anything with cents shows both ("€ 7,50").
const formatAmount = (value: number) =>
    (Number.isInteger(value) ? wholeCurrency : centsCurrency).format(value);

function Donate() {
    const [searchParams, setSearchParams] = useSearchParams();

    const [frequency, setFrequency] = useState<DonationFrequency>(DEFAULT_FREQUENCY);
    const [preset, setPreset] = useState<number | null>(DEFAULT_PRESET[DEFAULT_FREQUENCY]);
    const [custom, setCustom] = useState('');
    const [coverFees, setCoverFees] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>();

    const [result, setResult] = useState<PaymentResponse>();
    const [verifying, setVerifying] = useState(false);

    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled') === '1';
    const source: DonationSource = searchParams.get('bron') === 'qr' ? 'qr' : 'website';

    const amount = useMemo(() => {
        if (preset !== null) return preset;

        const parsed = Number.parseFloat(custom.replace(',', '.'));
        return Number.isFinite(parsed) ? parsed : NaN;
    }, [custom, preset]);

    const amountIsValid = Number.isFinite(amount) && amount >= MIN_AMOUNT && amount <= MAX_AMOUNT;

    const total = amountIsValid && coverFees ? amount + COVER_FEE : amount;

    const chooseFrequency = useCallback((next: DonationFrequency) => {
        setFrequency(next);
        setPreset(DEFAULT_PRESET[next]);
        setCustom('');
        setError(undefined);
    }, []);

    const choosePreset = useCallback((value: number) => {
        setPreset(value);
        setCustom('');
        setError(undefined);
    }, []);

    const changeCustom = useCallback((value: string) => {
        // Free-text field so Dutch visitors can type "7,50"; keep digits and one separator.
        const cleaned = value.replace(/[^\d.,]/g, '').replace(/[.,](?=.*[.,])/g, '');

        setCustom(cleaned);
        setPreset(null);
        setError(undefined);
    }, []);

    const donate = useCallback(async () => {
        if (!amountIsValid || submitting) return;

        setSubmitting(true);
        setError(undefined);

        const data: Payment = { amount, frequency, coverFees, source };

        try {
            const response = await fetch(`${process.env.PUBLIC_URL}/_donate.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(data),
            });

            const payload = await response.json().catch(() => undefined);

            if (!response.ok || !payload?.url) {
                setError(
                    payload?.error ??
                        'Er is iets misgegaan bij het starten van je gift. Probeer het later nog eens.'
                );
                setSubmitting(false);
                return;
            }

            window.location.href = (payload as PaymentCheckout).url;
        } catch {
            setError(
                'We konden geen verbinding maken met de betaaldienst. Controleer je internetverbinding en probeer het opnieuw.'
            );
            setSubmitting(false);
        }
    }, [amount, amountIsValid, coverFees, frequency, source, submitting]);

    useEffect(() => {
        if (!sessionId) return;

        let active = true;
        setVerifying(true);

        fetch(`${process.env.PUBLIC_URL}/_donation.php?session_id=${encodeURIComponent(sessionId)}`)
            .then((response) => (response.ok ? response.json() : undefined))
            .then((data?: PaymentResponse) => {
                if (active) setResult(data);
            })
            .catch(() => undefined)
            .finally(() => {
                if (active) setVerifying(false);
            });

        return () => {
            active = false;
        };
    }, [sessionId]);

    const dismiss = useCallback(() => {
        setResult(undefined);
        setSearchParams({}, { replace: true });
    }, [setSearchParams]);

    const ctaLabel = amountIsValid
        ? `Doneer ${formatAmount(total)}${frequency === 'month' ? ' per maand' : ''}`
        : 'Kies een bedrag';

    return (
        <div className="page-donate my-5">
            <div className="container text-start">
                {verifying ? (
                    <div className="alert alert-light border d-flex align-items-center" role="status">
                        <FontAwesomeIcon icon={faSpinner} spin className="me-2 text-body-secondary" />
                        We controleren je gift...
                    </div>
                ) : undefined}

                {result?.paid ? (
                    <div className="alert alert-success d-flex align-items-start" role="status">
                        <FontAwesomeIcon icon={faCircleCheck} className="me-2 mt-1" />
                        <div className="flex-grow-1">
                            <strong className="d-block">Hartelijk dank voor je gift!</strong>
                            {result.amount
                                ? `We hebben ${formatAmount(result.amount)}${
                                      result.frequency === 'month' ? ' per maand' : ''
                                  } ontvangen. `
                                : ''}
                            Je ontvangt een bevestiging per e-mail.
                        </div>
                        <button type="button" className="btn-close" aria-label="Sluiten" onClick={dismiss} />
                    </div>
                ) : undefined}

                {result && !result.paid ? (
                    <div className="alert alert-warning d-flex align-items-start" role="status">
                        <FontAwesomeIcon icon={faCircleInfo} className="me-2 mt-1" />
                        <div className="flex-grow-1">
                            <strong className="d-block">Je gift is nog niet afgerond</strong>
                            Sommige betaalmethoden hebben even tijd nodig. Ontvang je geen bevestiging?
                            Neem dan gerust contact met ons op.
                        </div>
                        <button type="button" className="btn-close" aria-label="Sluiten" onClick={dismiss} />
                    </div>
                ) : undefined}

                {canceled && !result ? (
                    <div className="alert alert-light border d-flex align-items-start" role="status">
                        <FontAwesomeIcon icon={faCircleInfo} className="me-2 mt-1 text-body-secondary" />
                        <div className="flex-grow-1">
                            Je hebt de betaling afgebroken. Er is niets afgeschreven &mdash; je kunt het
                            hieronder gerust opnieuw proberen.
                        </div>
                        <button type="button" className="btn-close" aria-label="Sluiten" onClick={dismiss} />
                    </div>
                ) : undefined}

                <p>
                    Wil je onderdeel zijn van onze missie en ondersteunen met een financiële bijdrage?
                    Elke gift helpt ons om lesmateriaal te kopen en praktische hulp te bieden aan
                    kinderen, mindervaliden en andersbegaafden in de Arabische wereld.
                </p>
            </div>

            <div className="container">
                <div className="donate-card card border-0 shadow-sm">
                    <div className="card-body p-4 p-md-5">
                        <h2 className="h4 mb-4">Geef een gift</h2>

                        <fieldset className="mb-4">
                            <legend className="form-label fw-semibold">Hoe vaak wil je geven?</legend>
                            <div className="donate-frequency btn-group w-100" role="group">
                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="donate-frequency"
                                    id="donate-frequency-once"
                                    autoComplete="off"
                                    checked={frequency === 'once'}
                                    onChange={() => chooseFrequency('once')}
                                />
                                <label className="btn" htmlFor="donate-frequency-once">
                                    Eenmalig
                                </label>

                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="donate-frequency"
                                    id="donate-frequency-month"
                                    autoComplete="off"
                                    checked={frequency === 'month'}
                                    onChange={() => chooseFrequency('month')}
                                />
                                <label className="btn" htmlFor="donate-frequency-month">
                                    Maandelijks
                                </label>
                            </div>
                            <div className="form-text mt-2">
                                {frequency === 'month'
                                    ? 'Een vast bedrag per maand geeft ons de zekerheid om vooruit te plannen. Je kunt altijd stoppen.'
                                    : 'Een eenmalige gift, direct en zonder verplichtingen.'}
                            </div>
                        </fieldset>

                        <fieldset className="mb-4">
                            <legend className="form-label fw-semibold">Kies een bedrag</legend>
                            <div className="donate-amounts">
                                {PRESETS[frequency].map((value) => (
                                    <div key={value} className="donate-amount">
                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="donate-amount"
                                            id={`donate-amount-${value}`}
                                            autoComplete="off"
                                            checked={preset === value}
                                            onChange={() => choosePreset(value)}
                                        />
                                        <label
                                            className="btn btn-outline-primary w-100"
                                            htmlFor={`donate-amount-${value}`}
                                        >
                                            {formatAmount(value)}
                                            {frequency === 'month' ? (
                                                <span className="donate-amount-suffix">p/m</span>
                                            ) : undefined}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </fieldset>

                        <div className="mb-4">
                            <label className="form-label fw-semibold" htmlFor="donate-custom">
                                Of vul zelf een bedrag in
                            </label>
                            <div className="input-group donate-custom">
                                <span className="input-group-text" aria-hidden="true">
                                    &euro;
                                </span>
                                <input
                                    id="donate-custom"
                                    className="form-control"
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    placeholder="Ander bedrag"
                                    aria-describedby="donate-custom-help"
                                    value={custom}
                                    onChange={(e) => changeCustom(e.target.value)}
                                />
                                {frequency === 'month' ? (
                                    <span className="input-group-text">per maand</span>
                                ) : undefined}
                            </div>
                            {custom && !amountIsValid ? (
                                <div id="donate-custom-help" className="form-text text-danger">
                                    Vul een bedrag in tussen {formatAmount(MIN_AMOUNT)} en{' '}
                                    {formatAmount(MAX_AMOUNT)}.
                                </div>
                            ) : undefined}
                        </div>

                        <div className="mb-4">
                            <div className="form-check d-inline-flex align-items-center gap-2 ps-0">
                                <input
                                    type="checkbox"
                                    className="form-check-input float-none m-0"
                                    id="donate-cover-fees"
                                    checked={coverFees}
                                    onChange={(e) => setCoverFees(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="donate-cover-fees">
                                    Ik dek de transactiekosten ({formatAmount(COVER_FEE)}
                                    {frequency === 'month' ? ' per maand' : ''})
                                </label>
                            </div>
                            <div className="form-text">
                                Elke gift kost ons iets aan betaalkosten. Dek je ze mee, dan gaat je hele
                                bedrag naar het werk. De kosten verschillen per betaalmethode; wat
                                overblijft besteden we aan de programma&rsquo;s.
                            </div>
                        </div>

                        {error ? (
                            <div className="alert alert-danger d-flex align-items-start" role="alert">
                                <FontAwesomeIcon icon={faCircleExclamation} className="me-2 mt-1" />
                                <div>{error}</div>
                            </div>
                        ) : undefined}

                        <button
                            type="button"
                            className="btn btn-primary btn-lg w-100 donate-submit"
                            disabled={!amountIsValid || submitting}
                            onClick={donate}
                        >
                            {submitting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                    Een moment geduld...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faHeart} className="me-2" />
                                    {ctaLabel}
                                </>
                            )}
                        </button>

                        <div className="donate-trust mt-3">
                            <span>
                                <FontAwesomeIcon icon={faLock} className="me-2 donate-lock" />
                                Beveiligde betaling via Stripe
                            </span>
                            <span>{TRUST_METHODS[frequency]}</span>
                        </div>

                        <p className="form-text mt-3 mb-0">
                            Stichting Sarfath heeft een ANBI-status. Je gift is daarom mogelijk aftrekbaar
                            bij de inkomstenbelasting.
                        </p>
                    </div>
                </div>
            </div>

            <div className="container text-start mt-5">
                <h3 className="h5">Liever zelf overmaken?</h3>
                <p className="mb-0">
                    Maak je gift over naar{' '}
                    <strong>NL87&nbsp;SNSB&nbsp;0952&nbsp;0075&nbsp;17</strong> t.n.v.{' '}
                    <strong>Stichting Sarfath</strong> met als omschrijving{' '}
                    <em>&ldquo;Gift voor Stichting Sarfath&rdquo;</em>. Een periodieke gift kun je ook
                    instellen via een automatische overschrijving naar dit rekeningnummer.
                </p>
            </div>

            <div className="container text-start border-top pt-3 mt-5">
                <p>
                    Geld is nodig om lesmateriaal te kunnen kopen en praktische hulp te bieden aan
                    mindervaliden en andersbegaafden. Bid daarnaast als je wilt ook met ons mee:
                </p>
                <ol type="1">
                    <li>Bid dat de mensen in de Arabische wereld Jezus mogen leren kennen.</li>
                    <li>
                        Bid dat ook de (arme) kinderen in de Arabische wereld, die om welke reden dan ook
                        niet mee kunnen komen, toegang krijgen tot onderwijs.
                    </li>
                    <li>
                        Bid mee voor een hoopvolle toekomst voor kinderen, mindervaliden en
                        andersbegaafden in de Arabische wereld.
                    </li>
                </ol>
                <p>
                    Heb je ervaring in lesgeven of in het ontwikkelen van lesmateriaal? Dan kunnen we je
                    hulp misschien wel goed gebruiken voor het ontwikkelen van nieuw lesmateriaal en het
                    lesgeven aan kinderen in de Arabische wereld of het trainen van leraren. Vul het
                    contactformulier als je enthousiast wordt van het idee om dit met ons te gaan doen.
                    Dit kan voor een stage van een aantal weken zijn, of voor een langere periode. Alle
                    aangeboden hulp waarderen wij!
                </p>

                <div className="d-flex justify-content-around mt-5">
                    <img className="donate-icon" src={iconPraying} alt="praying" />
                    <img className="donate-icon" src={iconDove} alt="dove" />
                    <img className="donate-icon" src={iconDonation} alt="donation" />
                </div>
            </div>
        </div>
    );
}

export default Donate;

